import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  ActivateDelegationGQL,
  CreateDelegationGQL,
  CreateDelegationInput,
  DeactivateDelegationGQL,
  DelegationStatusFilter,
  GetMyDelegationsGQL,
  GetOrganizationDelegationsGQL,
  RevokeDelegationGQL,
  RevokeDelegationInput,
  ToggleDelegationInput,
} from '@generated/graphql';

interface DelegationQueryParams {
  take?: number;
  skip?: number;
  status?: DelegationStatusFilter;
}

@Injectable({
  providedIn: 'root',
})
export class DelegationsService {
  private readonly getMyDelegationsGQL = inject(GetMyDelegationsGQL);
  private readonly getOrganizationDelegationsGQL = inject(GetOrganizationDelegationsGQL);
  private readonly createDelegationGQL = inject(CreateDelegationGQL);
  private readonly revokeDelegationGQL = inject(RevokeDelegationGQL);
  private readonly activateDelegationGQL = inject(ActivateDelegationGQL);
  private readonly deactivateDelegationGQL = inject(DeactivateDelegationGQL);

  getMyDelegations(params: DelegationQueryParams = {}) {
    return this.getMyDelegationsGQL
      .watch(
        {
          take: params.take ?? 25,
          skip: params.skip ?? 0,
          filters: params.status ? { status: params.status } : null,
        },
        { fetchPolicy: 'network-only' },
      )
      .valueChanges.pipe(
        map((result) => ({
          delegations: result.data?.myDelegations.edges.map((e) => e.node) ?? [],
          totalCount: result.data?.myDelegations.totalCount ?? 0,
          hasNextPage: result.data?.myDelegations.pageInfo.hasNextPage ?? false,
          loading: result.loading,
        })),
      );
  }

  getOrganizationDelegations(params: DelegationQueryParams = {}) {
    return this.getOrganizationDelegationsGQL
      .watch(
        {
          take: params.take ?? 25,
          skip: params.skip ?? 0,
          filters: params.status ? { status: params.status } : null,
        },
        { fetchPolicy: 'network-only' },
      )
      .valueChanges.pipe(
        map((result) => ({
          delegations: result.data?.organizationDelegations.edges.map((e) => e.node) ?? [],
          totalCount: result.data?.organizationDelegations.totalCount ?? 0,
          hasNextPage: result.data?.organizationDelegations.pageInfo.hasNextPage ?? false,
          loading: result.loading,
        })),
      );
  }

  createDelegation(input: CreateDelegationInput) {
    return this.createDelegationGQL
      .mutate({ input }, { refetchQueries: ['GetMyDelegations', 'GetOrganizationDelegations'] })
      .pipe(map((result) => result.data?.createDelegation));
  }

  revokeDelegation(input: RevokeDelegationInput) {
    return this.revokeDelegationGQL
      .mutate({ input }, { refetchQueries: ['GetMyDelegations', 'GetOrganizationDelegations'] })
      .pipe(map((result) => result.data?.revokeDelegation));
  }

  activateDelegation(input: ToggleDelegationInput) {
    return this.activateDelegationGQL
      .mutate({ input }, { refetchQueries: ['GetMyDelegations', 'GetOrganizationDelegations'] })
      .pipe(map((result) => result.data?.activateDelegation));
  }

  deactivateDelegation(input: ToggleDelegationInput) {
    return this.deactivateDelegationGQL
      .mutate({ input }, { refetchQueries: ['GetMyDelegations', 'GetOrganizationDelegations'] })
      .pipe(map((result) => result.data?.deactivateDelegation));
  }
}
